import { computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import store from '@/store';
import { useToast } from '@/composables/common/useToast';

/**
 * Totales del carrito de taquilla (mismas reglas que el widget legacy):
 * - productos: suma de units.
 * - precio: suma de precio_unidad × units.
 * - gastos: price_management por item, omitido cuando
 *   `entrada.price_management_interno` está activo.
 * @param {Array<object>} items
 * @returns {{productos: number, precio: number, gastos: number, total: number}}
 */
export function cartTotalsOf(items = []) {
    const safeItems = Array.isArray(items) ? items : [];
    const subItems = safeItems.flatMap(evento => (evento && Array.isArray(evento.items) ? evento.items : []));

    const productos = subItems.reduce((total, sub) => total + (sub.units || 0), 0);
    const precio = subItems.reduce((total, sub) => total + ((sub.precio_unidad || 0) * (sub.units || 0)), 0);
    const gastos = subItems.reduce((total, sub) => {
        const entrada = sub.entrada || {};
        if (entrada.price_management_interno) return total;
        return total + (entrada.price_management || 0);
    }, 0);

    return { productos, precio, gastos, total: precio + gastos };
}

/**
 * Formatea los segundos restantes del carrito como m:ss.
 * @param {number|null} seconds
 * @returns {string}
 */
export function formatRemaining(seconds) {
    if (seconds == null) return '';
    const safe = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(safe / 60);
    const rest = safe % 60;
    return minutes + ':' + (rest < 10 ? '0' : '') + rest;
}

/**
 * Carrito de taquilla del header (misma lógica de store que el widget legacy,
 * sin bootstrap): el timer real lo decrementa el store (START_CART_TIMER).
 */
export function useAdminCart() {
    const toast = useToast();

    const carrito = computed(() => store.getters.getCarritoActivo);
    const items = computed(() => {
        const value = store.getters.getCarritoItems;
        return Array.isArray(value) ? value : [];
    });
    const totals = computed(() => cartTotalsOf(items.value));
    const remaining = computed(() => (carrito.value ? carrito.value.seconds_left : null));

    function fetchActiveCart() {
        return store.dispatch('TAQUILLA_CARRITO_ACTIVO', { filters: {} }).catch(() => {
            // Silencioso: sin carrito no hay badge ni panel con datos.
        });
    }

    function deleteItem(itemId) {
        const cartId = carrito.value && carrito.value.id;
        if (!cartId) return Promise.resolve();

        return store.dispatch('TAQUILLA_CARRITO_DELETE_ITEMS', { filters: {}, cart_id: cartId, item_id: itemId })
            .then(() => {
                toast.success('Entrada borrada correctamente');
                return store.dispatch('TAQUILLA_CARRITO_ITEMS', { cart_id: cartId, filters: {} });
            })
            .then(() => {
                if (items.value.length === 0) {
                    return store.dispatch('TAQUILLA_CARRITO_ELIMINAR', { cart_id: cartId, filters: {} });
                }
                return null;
            })
            .catch(error => {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Error actualizando carrito online:', error);
                }
            });
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            fetchActiveCart();
        });
    }

    return { carrito, items, totals, remaining, fetchActiveCart, deleteItem };
}
